import React from 'react'
import { List, useTable, ShowButton, EditButton, DeleteButton, DateField } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderAvatarName } from '../../theme/columns'
import { useRowShow, ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { UsersRound, HandHeart } from '../../components/icons'

type Mentorship = Tables<'mentorships'>
type Profile = Tables<'profiles'>

export function MentorshipList() {
  const { tableProps } = useTable<Mentorship>({
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  })
  const onRow = useRowShow('mentorships')

  // Summary deck — bounded count queries (pageSize:1 → PostgREST returns the
  // total without the rows). Replaces the old unbounded useList over the whole
  // `mentorships` table. Total mentors + the `available = true` filtered count
  // run in parallel as two tiny requests.
  const { result: totalRes } = useList<Mentorship>({
    resource: 'mentorships',
    pagination: { pageSize: 1 },
  })
  const { result: availableRes } = useList<Mentorship>({
    resource: 'mentorships',
    filters: [{ field: 'available', operator: 'eq', value: true }],
    pagination: { pageSize: 1 },
  })
  const total = totalRes?.total ?? 0
  const available = availableRes?.total ?? 0

  // Resolve user_id -> name, scoped to ONLY the members on the current page
  // (IN filter), not the whole profiles table.
  const rows = (tableProps.dataSource ?? []) as Mentorship[]
  const userIds = React.useMemo(
    () => Array.from(new Set(rows.map((m) => m.user_id).filter(Boolean))) as string[],
    [rows],
  )
  const { result: profileRes } = useList<Profile>({
    resource: 'profiles',
    filters: userIds.length ? [{ field: 'user_id', operator: 'in', value: userIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: userIds.length > 0 },
  })
  const profileMap = React.useMemo<Record<string, string>>(() => {
    const items = (profileRes?.data ?? []) as Profile[]
    return Object.fromEntries(items.filter((p) => p.user_id != null).map((p) => [p.user_id as string, p.name]))
  }, [profileRes])

  const stats: Stat[] = [
    { label: 'Total Mentors', value: total.toLocaleString(), icon: UsersRound, tone: 'gold' },
    {
      label: 'Available',
      value: available.toLocaleString(),
      hint: 'open to mentees',
      icon: HandHeart,
      tone: 'green',
    },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Mentorship',
        description:
          'Alumni who have opened their door to mentor the next generation. Each lists a headline of what they offer; availability shows who is ready to take on a mentee right now.',
      }}
      stats={stats}
      statColumns={2}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Mentorship>
              dataIndex="user_id"
              title="Member"
              render={(userId: string) => renderAvatarName(profileMap[userId] ?? userId)}
            />
            <Table.Column<Mentorship>
              dataIndex="headline"
              title="Headline"
              render={(headline: string | null) =>
                headline ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 500, color: brand.ink }}>{headline}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Mentorship>
              dataIndex="available"
              title="Available"
              render={(available: boolean) => (
                <Pill tone={available ? 'green' : 'neutral'}>{available ? 'Available' : 'Paused'}</Pill>
              )}
            />
            <Table.Column<Mentorship>
              dataIndex="created_at"
              title="Joined"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Mentorship>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Mentorship) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
